import { BRAND_NAME } from "../../constants/brand";
import { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Divider, Grid, IconButton, InputAdornment, Link, Typography, useMediaQuery } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import signinStyle from '../../Styles/ComponentStyles/Signin.module.scss';
import { useSnackbar } from '../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import { UserContext, UserContextPayload } from '../../Context/UserDetailContext';
import newRBlogo from '../../assets/Images/mar-lockup-white.svg';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import MovingIcon from '@mui/icons-material/Moving';
import EmailIcon from '@mui/icons-material/Email';
import HttpsIcon from '@mui/icons-material/Https';
import GlobalTextfield from '../../GlobalComponent/GlobalTextfield/GlobalTextfield';
import globalStyles from '../../Styles/ComponentStyles/global.module.scss';

const points = [
    {
        img: MovingIcon,
        text: "Income Gap Supplementation"
    },
    {
        img: CalculateOutlinedIcon,
        text: "Roth Conversion Analysis"
    },
    {
        img: ShieldOutlinedIcon,
        text: "Qualified Account Spend Down"
    }
];

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { showSnackbar } = useSnackbar();

    const { setFlagLoader, resetPasswordAPI } = useContext(UserContext) as UserContextPayload

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [userDetails, setUserDetails] = useState<any>({
        "token": "",
        "email": "",
        "password": "",
        "confirm_password": ""
    })
    const isLessThan1024 = useMediaQuery('(max-width:1024px)')

    // useEffect(() => {
    //     if (window?.location?.hostname == "ba.retirementbrain.ai") {
    //         setCurrentLogo(signinBALogo)
    //     } else if (window?.location?.hostname == "admin.retirementbrain.ai" || window?.location?.hostname == "dev.retirementbrain.ai") {
    //         setCurrentLogo(signinRBLogo)
    //     } else {
    //         setCurrentLogo(signinRBLogo)
    //     }
    // }, [window?.location?.hostname])

    useEffect(() => {
        let token = searchParams.get("verify_token")
        let email = searchParams.get("email")
        if (!token || !email) {
            navigate('/login')
            return
        }
        setUserDetails({
            "token": token,
            "email": email,
            "password": "",
            "confirm_password": ""
        })
    }, [])

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleFieldDataChange = (fieldName: string, value: string) => {
        setUserDetails((prevDetails: any) => ({
            ...prevDetails,
            [fieldName]: value,
        }));
    };

    const handleClickShowPassword = () => {
        setShowPassword((prev) => !prev);
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword((prev) => !prev)
    }

    const handleResetPassword = async () => {
        if (!userDetails?.password || !userDetails?.confirm_password) {
            showSnackbar("Please fill in all the fields", "error");
            return;
        }
        try {
            setFlagLoader(true)
            const response = await resetPasswordAPI(userDetails);
            if (response?.status) {
                setFlagLoader(false)
                showSnackbar("Password reset successful", "success");
                navigate('/login')
            } else {
                setFlagLoader(false)
                showSnackbar(response?.error?.message || "Error resetting password!", "error")
            }
        } catch (error) {
            setFlagLoader(false)
            console.log(error, "Error fetching data!")
        } finally {
            setFlagLoader(false)
        }
    }

    const renderResetContent = (isSmallScreen: boolean) => {
        const titleCls = isSmallScreen ? signinStyle.welcomeTextForSmallScreen : signinStyle.welcomeText;
        const subCls = isSmallScreen ? signinStyle.rightSideSubTextForSmallScreen : signinStyle.rightSideSubText;
        const disclaimerHeadingCls = isSmallScreen ? signinStyle.disclaimerHeadingTextForSmallScreen : signinStyle.disclaimerHeadingText;
        const disclaimerTextCls = isSmallScreen ? signinStyle.disclaimerTextForSmallScreen : signinStyle.disclaimerText;

        return (
            <Grid container gap={3}>
                <Grid item xs={12}>
                    <Grid container alignItems="center" className={signinStyle.welcomeTextContainer}>
                        <Grid item xs={12}>
                            <Typography component={'span'} className={titleCls}>
                                Reset Password
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <Typography className={subCls}>
                                Set a new password to continue
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container gap={"18px"}>
                        <Grid item xs={12}>
                            <GlobalTextfield
                                required
                                variant="outlined"
                                placeholder='Email Address'
                                fullWidth
                                stylingClassName={globalStyles.globalPFTextfield}
                                value={userDetails?.email || ""}
                                disabled
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <GlobalTextfield
                                required
                                variant="outlined"
                                placeholder='New Password'
                                type={showPassword ? 'text' : 'password'}
                                fullWidth
                                stylingClassName={globalStyles.globalPFTextfield}
                                autoComplete='new-password'
                                value={userDetails?.password || ""}
                                onChange={(e) => { handleFieldDataChange('password', e?.target?.value) }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <HttpsIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => handleClickShowPassword()}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <GlobalTextfield
                                required
                                variant="outlined"
                                placeholder='Confirm Password'
                                type={showConfirmPassword ? 'text' : 'password'}
                                fullWidth
                                stylingClassName={globalStyles.globalPFTextfield}
                                value={userDetails?.confirm_password || ""}
                                onChange={(e) => { handleFieldDataChange('confirm_password', e?.target?.value) }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <HttpsIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => handleClickShowConfirmPassword()}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant='contained'
                                size='large'
                                fullWidth
                                className={signinStyle.signinBtn}
                                onClick={() => handleResetPassword()}
                            >
                                Reset Password
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                <Divider
                    sx={{
                        opacity: 0.8,
                        borderColor: "#E5E7EB",
                        height: "1px",
                        width: "100%",
                        margin: "0 auto"
                    }}
                />
                <Grid item xs={12}>
                    <Grid container gap={1}>
                        <Grid item xs={12}>
                            <Typography className={disclaimerHeadingCls}>
                                Disclaimer
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography className={disclaimerTextCls}>
                                This presentation is a general discussion of the topic presented, based on our current understanding of applicable tax laws, regulations, and rulings.
                                This information and presentation in no way should be interpreted as investment or tax advice. In actual practice, the transactions discussed may be more complex and will require the attention and expertise of professional tax and legal advisors.
                                In order to comply with certain U.S. Treasury regulations, please be advised of the following: unless expressly stated otherwise, any reference to U.S. Federal tax information contained in these materials,
                                including any attachments, is not intended or written to be used, and cannot be used, by any person for the purpose of avoiding any penalties that may be imposed
                                by the Internal Revenue Service. Values and benefits are not guaranteed unless stated as guaranteed on illustrations.
                                Examples are used to illustrate the potential benefits of indexed universal life insurance / IRS SEC. 7702 & 72(e).
                                Portions of this presentation are based on NAIC compliant life insurance illustrations which must be present during the presentation and fully reviewed
                                for additional disclosures.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <Typography className={disclaimerTextCls}>
                                        © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography component="div" className={disclaimerTextCls}>
                                        <Link href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: isSmallScreen ? '#fff' : '#3B78CE', fontSize: 'inherit' }}>
                                            Terms and Conditions
                                        </Link>
                                        {' | '}
                                        <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: isSmallScreen ? '#fff' : '#3B78CE', fontSize: 'inherit' }}>
                                            Privacy Policy
                                        </Link>
                                        {' | '}
                                        <Link href="/user-agreement" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: isSmallScreen ? '#fff' : '#3B78CE', fontSize: 'inherit' }}>
                                            User Agreement
                                        </Link>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    return (
        <Grid
            container
            direction={"column"}
            className={signinStyle.signinMainContainer}
            sx={{
                ...(isLessThan1024 && {
                    background: 'linear-gradient(135deg, rgba(4, 56, 93, 0.9) 0%, rgba(4, 56, 93, 0.8) 50%, rgba(59, 120, 206, 0.7) 100%)',
                }),
            }}
        >
            <Grid item xs={12} className={signinStyle.signinMainContainerItem}>
                <Grid container wrap="nowrap" alignItems="stretch" className={signinStyle.signinMainContainerRow}>
                    {isLessThan1024 ?
                        <Grid item xs={12} className={signinStyle.smallScreenStyle} alignItems={"flex-start"}>
                            <Grid container className={signinStyle.smallScreenRightMainContainer}>
                                <Box
                                    component="img"
                                    alt="Logo"
                                    src={newRBlogo}
                                />
                                <Grid item xs={12} className={signinStyle.welcomeContainer}>
                                    {renderResetContent(true)}
                                </Grid>
                            </Grid>
                        </Grid>
                        :
                        <>
                            <Grid item xs={6} className={signinStyle.leftSection}>
                                <Grid container gap={4} padding={"100px"}>
                                    <Grid item xs={12}>
                                        <Box sx={{ position: 'absolute', top: 0, left: 0, zIndex: 2, padding: "50px 100px" }}>
                                            <Box
                                                component="img"
                                                alt="Logo"
                                                src={newRBlogo}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid container className={signinStyle.pointsContainer}>
                                            <Grid item xs={12}>
                                                <Typography component='span' className={signinStyle.title}>
                                                    Turn Complex Retirement Planning into Simple Visual Storytelling
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography className={signinStyle.subText}>
                                                    A powerful platform combining interactive financial calculators with innovative products for agents and consumers.
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} className={signinStyle.pointsWrapper}>
                                                <Grid container direction="column" gap={2}>
                                                    {points.map((item, index) => (
                                                        <Grid item xs={12} key={index}>
                                                            <Grid container className={signinStyle.pointItem}>
                                                                <Grid item className={signinStyle.iconWrapper}>
                                                                    <item.img sx={{ color: "#fff" }} />
                                                                </Grid>
                                                                <Grid item className={signinStyle.textWrapper}>
                                                                    {item.text}
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={6} className={signinStyle.rightSection} alignItems={"flex-start"}>
                                <Grid container className={signinStyle.rightMainContainer}>
                                    <Grid item xs={8} className={signinStyle.welcomeContainer}>
                                        {renderResetContent(false)}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </>
                    }
                </Grid>
            </Grid>
        </Grid>
    );
};

export default ResetPassword;